import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Package, Search, Filter, Layers, Gamepad2, BookOpen, Heart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const universTabs = [
  { key: '', label: 'Tout', icon: Package, active: 'bg-slate-900 text-white shadow-lg', inactive: 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50' },
  { key: 'tcg', label: 'TCG', icon: Layers, active: 'bg-violet-600 text-white shadow-lg shadow-violet-600/20', inactive: 'bg-white text-violet-600 border border-violet-200 hover:bg-violet-50' },
  { key: 'jeu_de_societe', label: 'Jeux de société', icon: Gamepad2, active: 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20', inactive: 'bg-white text-emerald-600 border border-emerald-200 hover:bg-emerald-50' },
  { key: 'bd_manga', label: 'BD & Manga', icon: BookOpen, active: 'bg-rose-600 text-white shadow-lg shadow-rose-600/20', inactive: 'bg-white text-rose-600 border border-rose-200 hover:bg-rose-50' },
];

const typeColors = {
  tcg: { badge: 'bg-violet-100 text-violet-700 border-violet-200', gradient: 'from-violet-50 to-violet-100/50' },
  jeu_de_societe: { badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', gradient: 'from-emerald-50 to-emerald-100/50' },
  bd_manga: { badge: 'bg-rose-100 text-rose-700 border-rose-200', gradient: 'from-rose-50 to-rose-100/50' },
};
const typeLabels = { tcg: 'TCG', jeu_de_societe: 'Jeu de société', bd_manga: 'BD & Manga' };

export default function Catalog() {
  const [extensions, setExtensions] = useState([]);
  const [tcgs, setTcgs] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeUnivers = searchParams.get('univers') || '';
  const [filters, setFilters] = useState({ tcg_id: '', product_type: activeUnivers, status: 'ouvert' });

  useEffect(() => {
    setFilters(f => ({ ...f, product_type: activeUnivers, tcg_id: '' }));
  }, [activeUnivers]);

  useEffect(() => {
    api.get('/tcgs').then(r => setTcgs(r.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.tcg_id) params.set('tcg_id', filters.tcg_id);
    if (filters.product_type) params.set('product_type', filters.product_type);
    if (filters.status) params.set('status', filters.status);
    api.get(`/extensions?${params.toString()}`).then(r => setExtensions(r.data)).catch(console.error);
  }, [filters]);

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const statusMap = { ouvert: 'bg-emerald-100 text-emerald-800 border-emerald-200', ferme: 'bg-rose-100 text-rose-800 border-rose-200', livre: 'bg-sky-100 text-sky-800 border-sky-200' };
  const statusLabel = { ouvert: 'Ouvert', ferme: 'Fermé', livre: 'Livré' };

  return (
    <div className="min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Catalogue</h1>
          <p className="text-base md:text-lg text-slate-500 mt-2">Parcourez toutes les précommandes disponibles</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="univers-tabs">
          {universTabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeUnivers === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setSearchParams(tab.key ? { univers: tab.key } : {})}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${isActive ? tab.active : tab.inactive}`}
                data-testid={`tab-${tab.key || 'all'}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-3 mb-10 p-4 bg-white rounded-2xl border border-slate-200" data-testid="catalog-filters">
          <Filter className="h-5 w-5 text-slate-400 self-center" />
          {activeUnivers === 'tcg' && (
            <Select value={filters.tcg_id} onValueChange={(v) => setFilters(f => ({ ...f, tcg_id: v === 'all' ? '' : v }))}>
              <SelectTrigger className="w-[180px] rounded-lg" data-testid="filter-tcg">
                <SelectValue placeholder="Tous les TCG" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les TCG</SelectItem>
                {tcgs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] rounded-lg" data-testid="filter-status">
              <SelectValue placeholder="Tous les statuts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="ouvert">Ouvert</SelectItem>
              <SelectItem value="ferme">Fermé</SelectItem>
              <SelectItem value="livre">Livré</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {extensions.map((ext, i) => {
            const tc = typeColors[ext.product_type] || typeColors.tcg;
            return (
              <motion.div key={ext.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05, duration: 0.3 }}>
                <Link to={`/extension/${ext.id}`} className="block bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group" data-testid={`catalog-card-${ext.id}`}>
                  {ext.image_url ? (
                    <div className="h-40 overflow-hidden">
                      <img src={ext.image_url} alt={ext.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className={`h-40 bg-gradient-to-br ${tc.gradient} flex items-center justify-center`}>
                      {ext.product_type === 'tcg' && <Layers className="h-10 w-10 text-violet-200" />}
                      {ext.product_type === 'jeu_de_societe' && <Gamepad2 className="h-10 w-10 text-emerald-200" />}
                      {ext.product_type === 'bd_manga' && <BookOpen className="h-10 w-10 text-rose-200" />}
                      {!ext.product_type && <Package className="h-10 w-10 text-slate-200" />}
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {ext.product_type === 'tcg' && ext.tcg_name && <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-xs">{ext.tcg_name}</Badge>}
                      {ext.product_type === 'jeu_de_societe' && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Jeu de société</Badge>}
                      {ext.product_type === 'bd_manga' && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">BD & Manga</Badge>}
                      <Badge className={`text-xs border ${statusMap[ext.status] || statusMap.ouvert}`}>{statusLabel[ext.status] || ext.status}</Badge>
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-violet-600 transition-colors leading-snug">{ext.name}</h3>
                    <div className="mt-3 space-y-1 text-sm text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Sortie : {formatDate(ext.release_date)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Package className="h-3.5 w-3.5" />
                        <span>{ext.items?.length || 0} produit{(ext.items?.length || 0) > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
        {extensions.length === 0 && (
          <div className="text-center py-20">
            <Search className="h-16 w-16 mx-auto text-slate-200" />
            <p className="mt-4 text-slate-500 text-lg">Aucune précommande trouvée.</p>
            <p className="text-slate-400 text-sm mt-1">Essayez de modifier vos filtres.</p>
          </div>
        )}
      </div>
    </div>
  );
}
